import { IconGear } from "@decibeltrade/react-icons";
import { Button } from "@decibeltrade/web-core/components";
import { insetFocusRing } from "@decibeltrade/web-core/recipes";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { TRADE_LAYOUT_COOKIE } from "@/app/(main)/trade/[[...market-name]]/layout-config";
import { openLayoutConfigurationsModal } from "@/components/modal/layout-configurations-modal";
import { Tooltip } from "@/components/tooltip";
import { NetworkDownTooltip, useIsNetworkDown } from "@/contexts/health";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useCookieState } from "@/hooks/storage";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { useCacheSessionKeysStorage } from "@/hooks/use-session-keys";
import { useTradingSound } from "@/hooks/use-trading-sound";

import { Clickable } from "../../clickable";
import { Checkbox } from "../../form/checkbox";
import { Popover, PopoverTriggerProps } from "../../popover";
import { SelectTradingMode } from "./select-trading-mode";

export const AdvancedSettings = ({ isMobile }: { isMobile?: boolean }) => {
  const t = useTranslations("advancedSettings");
  const [cacheSessionKeys, setCacheSessionKeys] = useCacheSessionKeysStorage();
  const { isMuted, setIsMuted } = useTradingSound();

  return (
    <Popover modal mountOnlyWhenVisible trigger={<SettingsTrigger />}>
      {({ setIsOpen }: PopoverTriggerProps) => (
        <div className={popoverContainerStyles}>
          <Checkbox
            checkboxPosition="right"
            name="session-keys-remember"
            checked={cacheSessionKeys}
            onChange={(checked) => {
              setCacheSessionKeys(checked === true);
            }}
            className={css({ w: "full", justifyContent: "space-between" })}
          >
            {t("rememberSessionKeys")}
          </Checkbox>

          <Checkbox
            checkboxPosition="right"
            name="mute-trading-sounds"
            checked={isMuted}
            onChange={(checked) => {
              setIsMuted(checked === true);
            }}
            className={css({ w: "full", justifyContent: "space-between" })}
          >
            {t("muteTradingSounds")}
          </Checkbox>

          {!isMobile && <SelectTradingMode />}

          {!isMobile && <MoreConfigurationsButton closePopover={() => setIsOpen(false)} />}
        </div>
      )}
    </Popover>
  );
};

interface SettingsTriggerProps
  extends React.PropsWithChildren, React.HTMLAttributes<HTMLButtonElement> {}

const SettingsTrigger = (props: SettingsTriggerProps) => {
  const isNetworkDown = useIsNetworkDown();
  const { writeDexSdk } = useWriteDexSdk();
  const { connected } = useDecibelWallet();
  const tGeneral = useTranslations("general");
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

  const content = (
    <Clickable
      className={triggerStyles}
      {...props}
      ref={setAnchorElement}
      disabled={isNetworkDown || !writeDexSdk}
    >
      <IconGear
        className={css({
          h: "20",
          w: "20",
        })}
      />
    </Clickable>
  );

  if (isNetworkDown) {
    return (
      <NetworkDownTooltip isNetworkDown={true} anchorElement={anchorElement}>
        {content}
      </NetworkDownTooltip>
    );
  } else if (!writeDexSdk) {
    const msg = connected ? tGeneral("enableTrading") : tGeneral("connectWallet");
    return (
      <Tooltip placement="bottom-end" content={msg} disabled={false} anchorElement={anchorElement}>
        {content}
      </Tooltip>
    );
  }
  return content;
};

const triggerStyles = cx(
  css({
    p: "8",
    m: "-8",
    "&[data-state='open'] svg": {
      transform: "rotate(90deg)",
    },
    "&[data-state='closed'] svg": {
      transform: "rotate(0deg)",
    },
    "& svg": {
      transitionPreset: "transform",
    },
  }),
  insetFocusRing(),
);

function MoreConfigurationsButton({ closePopover }: { closePopover: () => void }) {
  const t = useTranslations("advancedSettings");
  const [tradeLayout, setTradeLayout] = useCookieState(TRADE_LAYOUT_COOKIE);

  const handleClick = () => {
    closePopover();
    openLayoutConfigurationsModal({
      currentLayout: tradeLayout,
      onSave: setTradeLayout,
    });
  };

  return (
    <Button variant="secondary" fullWidth onClick={handleClick}>
      {t("moreConfigurations")}
    </Button>
  );
}

const popoverContainerStyles = stack({
  gap: "8",
  p: "8",
  bg: "background.primary",
  rounded: "200",
  shadow: "default",
  borderWidth: "100",
  borderColor: "border.secondary",
});
